/**
 * ReferenceImageDialog Component
 *
 * Drag-and-drop / file-picker dialog for selecting a reference image
 * for image-to-image generation. Bytes go into Vault Documents under
 * /My-Fias/Reference Images on the user's Personal Holder, and the
 * resulting `documentId` is handed back to the image-generator as
 * `referenceImageFileId`.
 *
 * The legacy "browse existing user_files" path was removed in the
 * Vault Documents cutover — re-uploading is the canonical flow now.
 * If we want a picker later, it'll be a Vault-aware picker that
 * scopes to `/My-Fias/Reference Images` on the active Holder.
 */

import { useState, useCallback, useRef, DragEvent } from 'react';
import { Upload, ImagePlus, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import type { ReferenceImage } from '../types/image-generator.types';

export interface ReferenceImageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReferenceSelected: (ref: ReferenceImage) => void;
}

const ACCEPTED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const MAX_REFERENCE_SIZE = 20 * 1024 * 1024; // 20 MB

async function sha256HexOf(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function ReferenceImageDialog({
  open,
  onOpenChange,
  onReferenceSelected,
}: ReferenceImageDialogProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!isUploading) onOpenChange(newOpen);
    },
    [isUploading, onOpenChange],
  );

  const uploadFile = useCallback(
    async (file: File) => {
      if (!ACCEPTED_IMAGE_TYPES.has(file.type)) {
        toast({
          title: 'Unsupported file type',
          description: 'Please select a PNG, JPEG, WebP, or GIF image.',
          variant: 'destructive',
        });
        return;
      }
      if (file.size > MAX_REFERENCE_SIZE) {
        toast({
          title: 'File too large',
          description: 'Reference images must be under 20 MB.',
          variant: 'destructive',
        });
        return;
      }
      setIsUploading(true);
      try {
        // Resolve the user's Personal Holder via the memberships
        // endpoint — same path the HolderSwitcher uses.
        const memberships = await api.listMyVaultMemberships();
        const personalHolder = memberships.personalHolder;
        if (!personalHolder) {
          throw new Error('Personal workspace not provisioned');
        }
        const sha256 = await sha256HexOf(file);
        const init = await api.initVaultDocumentUpload({
          holderId: personalHolder.holderId,
          name: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
          checksumSha256: sha256,
          tags: ['reference-image'],
          purpose: 'reference-image',
        });
        // The Vault upload-init returns a presigned PUT. The required
        // headers (Content-Type, Content-Length, x-amz-checksum-sha256,
        // x-amz-tagging: pending=true) are part of the signature; we
        // copy them through verbatim.
        const putResponse = await fetch(init.uploadUrl, {
          method: 'PUT',
          headers: init.requiredHeaders,
          body: file,
        });
        if (!putResponse.ok) {
          throw new Error(`Upload failed (HTTP ${putResponse.status})`);
        }
        await api.finalizeVaultDocumentUpload(init.document.documentId, {
          checksumSha256: sha256,
        });

        const thumbnailUrl = URL.createObjectURL(file);
        onReferenceSelected({
          fileId: init.document.documentId,
          thumbnailUrl,
          source: 'uploaded',
        });
        onOpenChange(false);
      } catch (err) {
        console.error('Failed to upload reference image:', err);
        toast({
          title: 'Upload failed',
          description: err instanceof Error ? err.message : 'An error occurred',
          variant: 'destructive',
        });
      } finally {
        setIsUploading(false);
      }
    },
    [onReferenceSelected, onOpenChange],
  );

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) uploadFile(file);
    },
    [uploadFile],
  );
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) uploadFile(file);
      e.target.value = '';
    },
    [uploadFile],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImagePlus className="h-5 w-5" />
            Reference Image
          </DialogTitle>
          <DialogDescription>
            Add a reference image for image-to-image generation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
              isDragging
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-muted-foreground/50 hover:bg-muted/30'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            {isUploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
                <p className="text-sm text-muted-foreground">Uploading...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm font-medium">Drop an image here</p>
                <p className="text-xs text-muted-foreground">
                  or click to browse. PNG, JPEG, WebP, GIF up to 20 MB.
                </p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={handleFileInputChange}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
